import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HelpCircle, Mail } from 'lucide-react';
import SectionWrapper from '../shared/SectionWrapper';

/* ------------------------------------------------------------------ */
/*  Animation orchestration                                           */
/* ------------------------------------------------------------------ */
const stagger = {
  parent: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.1 },
    },
  },
  child: {
    hidden: { opacity: 0, y: 28 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
    },
  },
};

/* ------------------------------------------------------------------ */
/*  FAQ data                                                          */
/* ------------------------------------------------------------------ */
const faqsLeft = [
  {
    question: 'What ESG frameworks does ESGCaffe support?',
    answer:
      'We support 35+ frameworks including GRI, SASB, TCFD, CSRD/ESRS, CDP, SEBI BRSR, and more. New frameworks are added regularly.',
  },
  {
    question: 'How does the AI assistant work?',
    answer:
      'Our AI is trained on comprehensive ESG knowledge bases and your organization\'s data. It can answer questions, generate reports, predict compliance gaps, and map data to frameworks \u2014 all in natural language.',
  },
  {
    question: 'Can I import existing ESG data?',
    answer:
      'Yes! ESGCaffe supports CSV, Excel, and API imports. Our AI automatically validates and maps your data to the correct framework fields.',
  },
  {
    question: 'How secure is my data?',
    answer:
      'We use 256-bit AES encryption, SOC 2 compliant infrastructure, role-based access control, and complete multi-tenant isolation. Your data never leaves your dedicated environment.',
  },
];

const faqsRight = [
  {
    question: 'How long does setup take?',
    answer:
      'Most organizations are fully operational within 15 minutes. Connect your data, select your frameworks, and start generating reports immediately.',
  },
  {
    question: 'Do you support carbon accounting?',
    answer:
      'Yes, comprehensive Scope 1, 2, and 3 emissions tracking with automated emission factors from DEFRA, EPA, and GHG Protocol databases.',
  },
  {
    question: 'Can multiple team members collaborate?',
    answer:
      'Absolutely. ESGCaffe supports unlimited users with granular role-based permissions, task assignments, review workflows, and audit trails.',
  },
  {
    question: 'What report formats are available?',
    answer:
      'Generate reports in PDF, DOCX, Excel, and XBRL formats. All reports are automatically formatted to meet framework-specific requirements.',
  },
];

/* ------------------------------------------------------------------ */
/*  Accordion item                                                    */
/* ------------------------------------------------------------------ */
const FAQItem = ({ faq, isOpen, onToggle }) => {
  return (
    <div
      className={`
        rounded-xl border bg-white dark:bg-slate-800 transition-all duration-300
        ${isOpen ? 'border-blue-200 dark:border-blue-700 shadow-sm' : 'border-gray-100 dark:border-slate-700 hover:border-gray-200 dark:hover:border-slate-600'}
      `}
    >
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-4 p-5 text-left"
      >
        <span className={`text-sm font-semibold leading-snug transition-colors duration-200 ${isOpen ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-slate-300'}`}>
          {faq.question}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="flex-shrink-0"
        >
          <ChevronDown className={`h-4 w-4 transition-colors duration-200 ${isOpen ? 'text-blue-500' : 'text-gray-400 dark:text-slate-500'}`} />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5">
              <p className="text-sm leading-relaxed text-gray-500 dark:text-slate-400">
                {faq.answer}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  FAQ Column                                                        */
/* ------------------------------------------------------------------ */
const FAQColumn = ({ faqs, columnKey }) => {
  const [openIndex, setOpenIndex] = useState(-1);

  return (
    <div className="flex flex-col gap-3">
      {faqs.map((faq, idx) => (
        <motion.div key={`${columnKey}-${idx}`} variants={stagger.child}>
          <FAQItem
            faq={faq}
            isOpen={openIndex === idx}
            onToggle={() => setOpenIndex(openIndex === idx ? -1 : idx)}
          />
        </motion.div>
      ))}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
const FAQSection = () => {
  return (
    <SectionWrapper id="faq" bgClass="bg-white dark:bg-[#0a0f1a]" padding="py-10 md:py-12">
      <motion.div
        variants={stagger.parent}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-60px' }}
        className="flex flex-col items-center"
      >
        {/* Pill badge */}
        <motion.div variants={stagger.child}>
          <span className="inline-flex items-center gap-2 rounded-full bg-violet-50 dark:bg-violet-900/30 border border-violet-200/60 dark:border-violet-700/60 px-4 py-1.5 text-xs font-semibold tracking-wide text-violet-700 dark:text-violet-300 uppercase">
            <HelpCircle className="h-3.5 w-3.5" />
            FAQ
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h2
          variants={stagger.child}
          className="mt-6 text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white text-center"
        >
          Frequently <span className="bg-gradient-to-r from-blue-600 to-indigo-500 bg-clip-text text-transparent">Asked Questions</span>
        </motion.h2>

        {/* -------- FAQ grid -------- */}
        <div className="mt-14 w-full grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          <FAQColumn faqs={faqsLeft} columnKey="left" />
          <FAQColumn faqs={faqsRight} columnKey="right" />
        </div>

        {/* -------- Bottom CTA -------- */}
        <motion.div
          variants={stagger.child}
          className="mt-14 flex flex-col sm:flex-row items-center gap-2 text-sm text-gray-500 dark:text-slate-400"
        >
          <span>Still have questions?</span>
          <a
            href="mailto:support@esgcaffe.ai"
            className="group inline-flex items-center gap-1.5 font-semibold text-blue-600 transition-colors duration-200 hover:text-blue-700"
          >
            <Mail className="h-3.5 w-3.5" />
            support@esgcaffe.ai
          </a>
        </motion.div>
      </motion.div>
    </SectionWrapper>
  );
};

export default FAQSection;
